// ─── src/components/community/CommunityFeed.jsx ─────────────────────────────
// The live editorial feed on /community.
// Aesthetic: Soho House × Vogue Weddings × Bloomberg, dark, curated, cinematic.
//
// Layout:
//   Featured post , full width, immersive hero card
//   Grid (2-col)  , standard editorial cards
//   Load more     , "See more from the community"
//
// Vendor posting lives inside this file as CreatePostModal.
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  fetchPublishedPosts,
  createCommunityPost,
  uploadCommunityImage,
  reactToPost,
  fetchSessionReactions,
  getSessionKey,
  timeAgo,
  POST_TYPES,
  POST_TYPE_LIST,
} from '../../services/communityPostsService';

// ── Design tokens (match CommunityIntelligencePage) ───────────────────────────
const GD          = "'Gilda Display', Georgia, serif";
const NU          = "'Nunito', sans-serif";
const GOLD        = '#d39a21';
const GOLD_DIM    = 'rgba(211,154,33,0.1)';
const GOLD_BORDER = 'rgba(211,154,33,0.22)';
// Theme-aware: these reference the --ci-* CSS variables set by the parent
// CommunityIntelligencePage root, so this section follows the lights toggle.
// Dark values are the fallbacks (used if rendered outside that page).
const INK         = 'var(--ci-ink, #0B0A08)';
const SURFACE     = 'var(--ci-surface, #111009)';
const SURFACE2    = 'var(--ci-surface2, #161410)';
const BORDER      = 'var(--ci-border, rgba(255,255,255,0.06))';
const BORDER2     = 'var(--ci-border2, rgba(255,255,255,0.1))';
const CREAM       = 'var(--ci-text, #F2EFEA)';
const WARM        = 'var(--ci-text2, #B8AFA4)';
const MUTED       = 'var(--ci-muted, #7A7068)';

const TIER_COLOR = {
  excellence:    GOLD,
  senior_member: '#60a5fa',
  member:        MUTED,
};

// ── Type filter pills ──────────────────────────────────────────────────────────
const FILTER_OPTIONS = [
  { key: 'all',             label: 'All'             },
  { key: 'venue_spotlight', label: 'Venues'          },
  { key: 'behind_scenes',   label: 'Behind the Scenes' },
  { key: 'editorial',       label: 'Editorial'       },
  { key: 'announcement',    label: 'News'            },
  { key: 'inspiration',     label: 'Inspiration'     },
];

// ─────────────────────────────────────────────────────────────────────────────
// POST TYPE BADGE
// ─────────────────────────────────────────────────────────────────────────────
function PostTypeBadge({ type, size = 'sm' }) {
  const cfg = POST_TYPES[type] || POST_TYPES.editorial;
  const fontSize = size === 'sm' ? 9 : 10;
  return (
    <span style={{
      fontFamily: NU, fontSize, fontWeight: 700,
      letterSpacing: '0.12em', textTransform: 'uppercase',
      color: cfg.color,
      background: 'rgba(0,0,0,0.45)',
      backdropFilter: 'blur(8px)',
      borderRadius: 3, padding: size === 'sm' ? '4px 8px' : '5px 10px',
      border: `1px solid ${cfg.color}22`,
      whiteSpace: 'nowrap',
    }}>
      {cfg.icon} {cfg.label}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// AUTHOR ROW
// ─────────────────────────────────────────────────────────────────────────────
function AuthorRow({ post, light = false, size = 'sm' }) {
  const tierColor = TIER_COLOR[post.author_tier] || MUTED;
  const nameColor = light ? 'rgba(255,255,255,0.9)' : WARM;
  const metaColor = light ? 'rgba(255,255,255,0.5)' : MUTED;
  const avSize    = size === 'sm' ? 28 : 36;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      {/* Avatar */}
      <div style={{
        width: avSize, height: avSize, borderRadius: '50%',
        background: post.author_avatar ? 'transparent' : 'rgba(211,154,33,0.15)',
        border: `1.5px solid ${tierColor}44`,
        overflow: 'hidden', flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {post.author_avatar ? (
          <img src={post.author_avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <span style={{ fontFamily: GD, fontSize: avSize * 0.4, color: tierColor, fontStyle: 'italic' }}>
            {(post.author_name || 'A')[0]}
          </span>
        )}
      </div>
      {/* Name + role */}
      <div>
        <div style={{ fontFamily: NU, fontSize: size === 'sm' ? 11 : 13, fontWeight: 700, color: nameColor }}>
          {post.author_name || 'LWD Member'}
          {post.author_tier === 'excellence' && (
            <span style={{ color: GOLD, marginLeft: 5, fontSize: 8 }}>✦</span>
          )}
        </div>
        {post.author_role && (
          <div style={{ fontFamily: NU, fontSize: 9, color: metaColor, letterSpacing: '0.04em' }}>
            {post.author_role}
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// REACTION ROW
// ─────────────────────────────────────────────────────────────────────────────
function ReactionRow({ post, liked, saved, onLike, onSave, light = false }) {
  const mutedC = light ? 'rgba(255,255,255,0.5)' : MUTED;
  const activeC = GOLD;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
      {/* Like */}
      <button
        onClick={e => { e.stopPropagation(); onLike(); }}
        style={{
          background: 'none', border: 'none', padding: 0, cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 5,
          color: liked ? activeC : mutedC,
          transition: 'color 0.15s',
          fontFamily: NU, fontSize: 11,
        }}
        title="Appreciate"
      >
        <span style={{ fontSize: 13 }}>{liked ? '◆' : '◇'}</span>
        {(post.likes_count || 0) > 0 && (
          <span style={{ fontWeight: 600 }}>{post.likes_count}</span>
        )}
      </button>

      {/* Save */}
      <button
        onClick={e => { e.stopPropagation(); onSave(); }}
        style={{
          background: 'none', border: 'none', padding: 0, cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 5,
          color: saved ? activeC : mutedC,
          transition: 'color 0.15s',
          fontFamily: NU, fontSize: 11,
        }}
        title="Save"
      >
        <span style={{ fontSize: 13 }}>{saved ? '◈' : '◉'}</span>
        {(post.saves_count || 0) > 0 && (
          <span style={{ fontWeight: 600 }}>{post.saves_count}</span>
        )}
      </button>

      {/* Time */}
      <span style={{ fontFamily: NU, fontSize: 10, color: mutedC, marginLeft: 'auto' }}>
        {timeAgo(post.published_at)}
      </span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FEATURED POST CARD, full-width, immersive, cinematic
// ─────────────────────────────────────────────────────────────────────────────
function FeaturedPostCard({ post, liked, saved, onLike, onSave, onClick }) {
  const hasImage = post.cover_image_url || (post.media_urls?.[0]);
  const imgSrc   = post.cover_image_url || post.media_urls?.[0];
  const isQuote  = post.post_type === 'quote';

  return (
    <div
      onClick={onClick}
      style={{
        position: 'relative',
        width: '100%',
        borderRadius: 6,
        overflow: 'hidden',
        cursor: 'pointer',
        background: isQuote
          ? `linear-gradient(135deg, ${SURFACE2} 0%, #1a1714 100%)`
          : SURFACE2,
        border: post.is_featured ? `1px solid ${GOLD_BORDER}` : `1px solid ${BORDER}`,
        transition: 'border-color 0.2s',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.borderColor = post.is_featured
          ? GOLD
          : 'rgba(255,255,255,0.15)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.borderColor = post.is_featured
          ? GOLD_BORDER
          : BORDER;
      }}
    >
      {/* ── Image ── */}
      {hasImage && !isQuote && (
        <div style={{ position: 'relative', width: '100%', paddingBottom: '52%', overflow: 'hidden' }}>
          <img
            src={imgSrc}
            alt={post.title || ''}
            loading="eager"
            style={{
              position: 'absolute', inset: 0,
              width: '100%', height: '100%',
              objectFit: 'cover', display: 'block',
              transition: 'transform 0.5s ease',
            }}
            onMouseEnter={e => { e.target.style.transform = 'scale(1.03)'; }}
            onMouseLeave={e => { e.target.style.transform = 'scale(1)'; }}
          />
          {/* Gradient overlay */}
          <div style={{
            position: 'absolute', inset: 0,
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.12) 40%, rgba(11,10,8,0.82) 100%)',
          }} />
          {/* Badges overlay */}
          <div style={{
            position: 'absolute', top: 16, left: 16,
            display: 'flex', gap: 8, alignItems: 'center',
          }}>
            <PostTypeBadge type={post.post_type} size="sm" />
            {post.is_featured && (
              <span style={{
                fontFamily: NU, fontSize: 8, fontWeight: 700,
                letterSpacing: '0.15em', textTransform: 'uppercase',
                color: GOLD, background: 'rgba(211,154,33,0.15)',
                backdropFilter: 'blur(8px)',
                borderRadius: 3, padding: '4px 8px',
                border: `1px solid ${GOLD_BORDER}`,
              }}>
                ✦ Featured
              </span>
            )}
          </div>
          {/* Title over image */}
          {post.title && (
            <div style={{
              position: 'absolute', bottom: 0, left: 0, right: 0,
              padding: '24px 24px 20px',
            }}>
              <h2 style={{
                fontFamily: GD, fontStyle: 'italic',
                fontSize: 'clamp(20px, 2.5vw, 30px)',
                fontWeight: 400, color: '#F2EFEA', margin: 0, lineHeight: 1.25,
                textShadow: '0 2px 12px rgba(0,0,0,0.6)',
              }}>
                {post.title}
              </h2>
            </div>
          )}
        </div>
      )}

      {/* ── Quote card (no image) ── */}
      {isQuote && (
        <div style={{
          padding: '56px 40px',
          textAlign: 'center',
          borderBottom: `1px solid ${BORDER}`,
        }}>
          <div style={{
            fontFamily: GD, fontStyle: 'italic',
            fontSize: 'clamp(18px, 2.8vw, 34px)',
            fontWeight: 300, color: CREAM, lineHeight: 1.4,
            maxWidth: 700, margin: '0 auto',
          }}>
            "{post.caption || post.title}"
          </div>
          <div style={{
            marginTop: 20, fontFamily: NU, fontSize: 10,
            letterSpacing: '0.2em', textTransform: 'uppercase', color: GOLD,
          }}>
            {post.author_name}
            {post.author_role && ` · ${post.author_role}`}
          </div>
        </div>
      )}

      {/* ── Card body ── */}
      <div style={{ padding: '16px 20px 18px' }}>
        {/* Caption (shown when no title-over-image) */}
        {!hasImage && post.title && !isQuote && (
          <h2 style={{
            fontFamily: GD, fontStyle: 'italic', fontSize: 22,
            fontWeight: 400, color: CREAM, margin: '0 0 8px', lineHeight: 1.3,
          }}>
            {post.title}
          </h2>
        )}
        {post.caption && !isQuote && (
          <p style={{
            fontFamily: NU, fontSize: 13, color: WARM,
            margin: '0 0 16px', lineHeight: 1.65,
            display: '-webkit-box', WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>
            {post.caption}
          </p>
        )}

        {/* Footer */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <AuthorRow post={post} size="sm" />
          <ReactionRow post={post} liked={liked} saved={saved} onLike={onLike} onSave={onSave} />
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// STANDARD POST CARD, 2-column grid
// ─────────────────────────────────────────────────────────────────────────────
function StandardPostCard({ post, liked, saved, onLike, onSave, onClick }) {
  const hasImage = post.cover_image_url || post.media_urls?.[0];
  const imgSrc   = post.cover_image_url || post.media_urls?.[0];
  const isQuote  = post.post_type === 'quote';

  return (
    <div
      onClick={onClick}
      style={{
        background: SURFACE2,
        borderRadius: 6,
        overflow: 'hidden',
        border: `1px solid ${BORDER}`,
        cursor: 'pointer',
        display: 'flex', flexDirection: 'column',
        transition: 'border-color 0.15s, transform 0.2s',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.borderColor = 'rgba(255,255,255,0.14)';
        e.currentTarget.style.transform = 'translateY(-3px)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.borderColor = BORDER;
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      {/* Image */}
      {hasImage && !isQuote && (
        <div style={{ position: 'relative', width: '100%', paddingBottom: '65%', overflow: 'hidden', flexShrink: 0 }}>
          <img
            src={imgSrc}
            alt={post.title || ''}
            loading="lazy"
            style={{
              position: 'absolute', inset: 0,
              width: '100%', height: '100%',
              objectFit: 'cover', display: 'block',
              transition: 'transform 0.4s ease',
            }}
            onMouseEnter={e => { e.target.style.transform = 'scale(1.06)'; }}
            onMouseLeave={e => { e.target.style.transform = 'scale(1)'; }}
          />
          {/* Dark gradient for legibility */}
          <div style={{
            position: 'absolute', inset: 0,
            background: 'linear-gradient(to bottom, transparent 50%, rgba(11,10,8,0.7) 100%)',
          }} />
          {/* Type badge */}
          <div style={{ position: 'absolute', top: 10, left: 10 }}>
            <PostTypeBadge type={post.post_type} size="sm" />
          </div>
          {post.is_featured && (
            <div style={{ position: 'absolute', top: 10, right: 10 }}>
              <span style={{
                fontFamily: NU, fontSize: 8, fontWeight: 700,
                color: GOLD, background: 'rgba(0,0,0,0.5)',
                backdropFilter: 'blur(6px)',
                borderRadius: 3, padding: '3px 7px',
                border: `1px solid ${GOLD_BORDER}`,
                letterSpacing: '0.12em',
              }}>
                ✦
              </span>
            </div>
          )}
        </div>
      )}

      {/* Quote card body */}
      {isQuote && (
        <div style={{
          padding: '32px 20px 24px', textAlign: 'center', flexShrink: 0,
          borderBottom: `1px solid ${BORDER}`,
          background: `linear-gradient(160deg, ${SURFACE2} 0%, #1a1714 100%)`,
        }}>
          <div style={{
            fontFamily: GD, fontStyle: 'italic', fontSize: 15,
            fontWeight: 300, color: CREAM, lineHeight: 1.6,
            marginBottom: 12,
          }}>
            "{post.caption || post.title}"
          </div>
          <PostTypeBadge type={post.post_type} size="sm" />
        </div>
      )}

      {/* Content */}
      <div style={{ padding: '14px 16px 14px', flex: 1, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {/* Type label (no image) */}
        {!hasImage && !isQuote && (
          <div>
            <PostTypeBadge type={post.post_type} size="sm" />
          </div>
        )}

        {/* Title */}
        {post.title && !isQuote && (
          <div style={{
            fontFamily: GD, fontStyle: 'italic', fontSize: 16,
            fontWeight: 400, color: CREAM, lineHeight: 1.3,
            display: '-webkit-box', WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>
            {post.title}
          </div>
        )}

        {/* Caption */}
        {post.caption && !isQuote && (
          <div style={{
            fontFamily: NU, fontSize: 11, color: WARM, lineHeight: 1.6,
            display: '-webkit-box', WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>
            {post.caption}
          </div>
        )}

        {/* Spacer */}
        <div style={{ flex: 1 }} />

        {/* Author */}
        <AuthorRow post={post} size="sm" />

        {/* Divider */}
        <div style={{ height: 1, background: BORDER }} />

        {/* Reactions */}
        <ReactionRow post={post} liked={liked} saved={saved} onLike={onLike} onSave={onSave} />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// POST DETAIL MODAL
// ─────────────────────────────────────────────────────────────────────────────
function PostDetailModal({ post, liked, saved, onLike, onSave, onClose }) {
  if (!post) return null;
  const imgSrc = post.cover_image_url || post.media_urls?.[0];
  const extras = (post.media_urls || []).slice(1);

  return (
    <div
      onClick={e => e.target === e.currentTarget && onClose()}
      style={{
        position: 'fixed', inset: 0, zIndex: 900,
        background: 'rgba(0,0,0,0.88)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '20px', backdropFilter: 'blur(4px)',
      }}
    >
      <div style={{
        background: SURFACE2, borderRadius: 10, width: '100%', maxWidth: 720,
        border: `1px solid ${BORDER2}`,
        maxHeight: '90vh', overflow: 'hidden',
        display: 'flex', flexDirection: 'column',
      }}>
        {/* Close */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 20px', borderBottom: `1px solid ${BORDER}`, flexShrink: 0,
        }}>
          <PostTypeBadge type={post.post_type} size="md" />
          <button onClick={onClose} style={{
            background: 'none', border: 'none', color: MUTED, fontSize: 18,
            cursor: 'pointer', padding: 4, lineHeight: 1,
          }}>
            ✕
          </button>
        </div>

        {/* Scrollable body */}
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {/* Main image */}
          {imgSrc && post.post_type !== 'quote' && (
            <img
              src={imgSrc}
              alt={post.title || ''}
              style={{ width: '100%', display: 'block', maxHeight: 420, objectFit: 'cover' }}
            />
          )}

          {/* Extra images */}
          {extras.length > 0 && (
            <div style={{ display: 'flex', gap: 4, padding: '4px' }}>
              {extras.map((url, i) => (
                <img
                  key={i} src={url} alt=""
                  style={{ width: `${100 / extras.length}%`, height: 120, objectFit: 'cover', borderRadius: 3 }}
                />
              ))}
            </div>
          )}

          {/* Content */}
          <div style={{ padding: '24px 28px' }}>
            {post.title && (
              <h2 style={{
                fontFamily: GD, fontStyle: 'italic', fontSize: 26,
                fontWeight: 400, color: CREAM, margin: '0 0 14px', lineHeight: 1.25,
              }}>
                {post.title}
              </h2>
            )}
            {post.caption && (
              <p style={{
                fontFamily: NU, fontSize: 14, color: WARM,
                margin: '0 0 16px', lineHeight: 1.7,
              }}>
                {post.caption}
              </p>
            )}
            {post.body_text && (
              <p style={{
                fontFamily: NU, fontSize: 13, color: WARM,
                margin: '0 0 20px', lineHeight: 1.8,
              }}>
                {post.body_text}
              </p>
            )}

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
              <AuthorRow post={post} size="md" />
              <ReactionRow post={post} liked={liked} saved={saved} onLike={onLike} onSave={onSave} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CREATE POST MODAL (vendor-facing)
// ─────────────────────────────────────────────────────────────────────────────
const INPUT_STYLE = {
  width: '100%', boxSizing: 'border-box',
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 4, color: CREAM,
  fontFamily: NU, fontSize: 13,
  padding: '9px 12px', outline: 'none',
};

function CreatePostModal({ vendor, onClose, onSubmitted }) {
  const [postType,   setPostType]   = useState('editorial');
  const [title,      setTitle]      = useState('');
  const [caption,    setCaption]    = useState('');
  const [images,     setImages]     = useState([]); // { file, preview, uploading, url }
  const [saving,     setSaving]     = useState(false);
  const [submitted,  setSubmitted]  = useState(false);
  const [dragOver,   setDragOver]   = useState(false);
  const fileInputRef                = useRef(null);

  const handleFiles = useCallback((files) => {
    const imgs = Array.from(files).filter(f => f.type.startsWith('image/')).slice(0, 5);
    const newImages = imgs.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      uploading: false,
      url: null,
    }));
    setImages(prev => [...prev, ...newImages].slice(0, 5));
  }, []);

  const removeImage = useCallback((i) => {
    setImages(prev => prev.filter((_, idx) => idx !== i));
  }, []);

  const handleSubmit = useCallback(async () => {
    if (saving) return;
    if (!caption.trim() && !title.trim() && images.length === 0) return;
    setSaving(true);

    // Upload images
    const uploadedUrls = [];
    for (const img of images) {
      if (img.url) {
        uploadedUrls.push(img.url);
      } else {
        const tempId = `draft_${Date.now()}`;
        const { url } = await uploadCommunityImage(img.file, tempId);
        if (url) uploadedUrls.push(url);
      }
    }

    const { error } = await createCommunityPost({
      vendorId:     vendor?.id,
      authorName:   vendor?.business_name || vendor?.name,
      authorRole:   vendor?.category,
      authorAvatar: vendor?.logo_url || vendor?.avatar_url,
      authorTier:   vendor?.tier,
      postType,
      title,
      caption,
      mediaUrls:    uploadedUrls,
      coverImageUrl: uploadedUrls[0] || null,
    });

    setSaving(false);
    if (!error) {
      setSubmitted(true);
      onSubmitted?.();
    }
  }, [saving, caption, title, images, vendor, postType, onSubmitted]);

  if (submitted) {
    return (
      <div style={{
        position: 'fixed', inset: 0, zIndex: 950,
        background: 'rgba(0,0,0,0.9)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
      }}>
        <div style={{
          background: SURFACE2, borderRadius: 12, maxWidth: 420, width: '100%',
          padding: '52px 40px', textAlign: 'center',
          border: `1px solid ${GOLD_BORDER}`,
        }}>
          <div style={{ fontSize: 32, color: GOLD, marginBottom: 16 }}>◈</div>
          <div style={{
            fontFamily: GD, fontStyle: 'italic', fontSize: 22, color: CREAM, marginBottom: 10,
          }}>
            Submitted for Review
          </div>
          <p style={{ fontFamily: NU, fontSize: 13, color: WARM, lineHeight: 1.7, marginBottom: 28 }}>
            Your post will appear in the community feed once it has been reviewed by the LWD editorial team.
          </p>
          <button
            onClick={onClose}
            style={{
              background: GOLD, border: 'none', borderRadius: 6,
              color: '#000', fontFamily: NU, fontSize: 12, fontWeight: 700,
              padding: '11px 28px', cursor: 'pointer',
            }}
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  const canSubmit = !saving && (caption.trim() || title.trim() || images.length > 0);

  return (
    <div
      onClick={e => e.target === e.currentTarget && onClose()}
      style={{
        position: 'fixed', inset: 0, zIndex: 950,
        background: 'rgba(0,0,0,0.88)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
      }}
    >
      <div style={{
        background: SURFACE2, borderRadius: 12, width: '100%', maxWidth: 560,
        border: `1px solid ${BORDER2}`,
        maxHeight: '90vh', display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{
          padding: '18px 24px', borderBottom: `1px solid ${BORDER}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
        }}>
          <div>
            <div style={{ fontFamily: GD, fontStyle: 'italic', fontSize: 20, color: CREAM }}>
              Share with the Community
            </div>
            <div style={{ fontFamily: NU, fontSize: 11, color: MUTED, marginTop: 2 }}>
              Your post will be reviewed before appearing in the feed
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: MUTED, fontSize: 20, cursor: 'pointer', padding: 4 }}>✕</button>
        </div>

        {/* Body */}
        <div style={{ overflowY: 'auto', flex: 1, padding: 24 }}>

          {/* Post type selector */}
          <div style={{ marginBottom: 22 }}>
            <div style={{
              fontFamily: NU, fontSize: 10, fontWeight: 700, letterSpacing: '0.1em',
              textTransform: 'uppercase', color: MUTED, marginBottom: 10,
            }}>
              Post Type
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {POST_TYPE_LIST.map(t => (
                <button
                  key={t.key}
                  onClick={() => setPostType(t.key)}
                  style={{
                    background: postType === t.key ? GOLD_DIM : 'rgba(255,255,255,0.03)',
                    border: postType === t.key ? `1px solid ${GOLD_BORDER}` : `1px solid ${BORDER}`,
                    borderRadius: 20, cursor: 'pointer',
                    fontFamily: NU, fontSize: 10, fontWeight: postType === t.key ? 700 : 400,
                    color: postType === t.key ? GOLD : MUTED,
                    padding: '6px 12px',
                    transition: 'all 0.12s',
                  }}
                >
                  {t.icon} {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Title */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontFamily: NU, fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: MUTED, marginBottom: 6 }}>
              Title (optional)
            </div>
            <input
              style={INPUT_STYLE}
              placeholder={postType === 'venue_spotlight'
                ? 'e.g. Villa Cora, Spring 2026'
                : postType === 'quote'
                  ? 'Leave blank to use caption as the quote'
                  : 'e.g. Why ceremony timing matters more than you think'}
              value={title}
              onChange={e => setTitle(e.target.value)}
            />
          </div>

          {/* Caption */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontFamily: NU, fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: MUTED, marginBottom: 6 }}>
              {postType === 'quote' ? 'The Quote' : 'Caption / Description'}
            </div>
            <textarea
              style={{ ...INPUT_STYLE, minHeight: 96, resize: 'vertical', lineHeight: 1.65 }}
              placeholder={postType === 'quote'
                ? '"The best venues are the ones you never want to leave."'
                : 'Share something worth knowing with the community...'}
              value={caption}
              onChange={e => setCaption(e.target.value)}
            />
          </div>

          {/* Image upload */}
          {postType !== 'quote' && (
            <div style={{ marginBottom: 4 }}>
              <div style={{ fontFamily: NU, fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: MUTED, marginBottom: 8 }}>
                Images (up to 5)
              </div>

              {/* Drop zone */}
              {images.length < 5 && (
                <div
                  onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={e => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    border: `1.5px dashed ${dragOver ? GOLD : 'rgba(255,255,255,0.15)'}`,
                    borderRadius: 6, padding: '28px 20px', textAlign: 'center',
                    cursor: 'pointer', background: dragOver ? GOLD_DIM : 'rgba(255,255,255,0.02)',
                    transition: 'all 0.15s', marginBottom: 12,
                  }}
                >
                  <div style={{ fontSize: 20, color: dragOver ? GOLD : MUTED, marginBottom: 6 }}>↑</div>
                  <div style={{ fontFamily: NU, fontSize: 12, color: dragOver ? GOLD : MUTED }}>
                    Drop images here or <span style={{ color: dragOver ? GOLD : WARM, textDecoration: 'underline' }}>browse</span>
                  </div>
                  <div style={{ fontFamily: NU, fontSize: 10, color: MUTED, marginTop: 4 }}>
                    JPEG, PNG, WebP · Max 10MB each
                  </div>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                style={{ display: 'none' }}
                onChange={e => handleFiles(e.target.files)}
              />

              {/* Image previews */}
              {images.length > 0 && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {images.map((img, i) => (
                    <div key={i} style={{ position: 'relative', flexShrink: 0 }}>
                      <img
                        src={img.preview}
                        alt=""
                        style={{
                          width: 72, height: 72, objectFit: 'cover',
                          borderRadius: 4, display: 'block',
                          border: i === 0 ? `2px solid ${GOLD}` : `2px solid ${BORDER}`,
                        }}
                      />
                      {i === 0 && (
                        <div style={{
                          position: 'absolute', bottom: 3, left: 3,
                          background: 'rgba(0,0,0,0.7)', borderRadius: 2,
                          fontFamily: NU, fontSize: 7, color: GOLD, padding: '1px 4px',
                        }}>
                          Cover
                        </div>
                      )}
                      <button
                        onClick={() => removeImage(i)}
                        style={{
                          position: 'absolute', top: 3, right: 3,
                          background: 'rgba(0,0,0,0.75)', border: 'none',
                          borderRadius: '50%', width: 16, height: 16,
                          color: '#fff', fontSize: 9, cursor: 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          lineHeight: 1,
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 24px', borderTop: `1px solid ${BORDER}`,
          display: 'flex', justifyContent: 'flex-end', gap: 10, flexShrink: 0,
        }}>
          <button onClick={onClose} style={{
            background: 'none', border: `1px solid ${BORDER}`,
            color: MUTED, borderRadius: 6, padding: '9px 18px',
            fontFamily: NU, fontSize: 12, cursor: 'pointer',
          }}>
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            style={{
              background: canSubmit ? GOLD : 'rgba(211,154,33,0.25)',
              border: 'none', borderRadius: 6,
              color: canSubmit ? '#000' : 'rgba(0,0,0,0.4)',
              fontFamily: NU, fontSize: 12, fontWeight: 700,
              padding: '9px 22px',
              cursor: canSubmit ? 'pointer' : 'default',
              transition: 'all 0.15s',
            }}
          >
            {saving ? 'Submitting...' : 'Submit for Review'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// EMPTY STATE
// ─────────────────────────────────────────────────────────────────────────────
function EmptyState({ onPost }) {
  return (
    <div style={{
      padding: '64px 24px', textAlign: 'center',
      border: `1px dashed rgba(255,255,255,0.08)`,
      borderRadius: 8,
    }}>
      <div style={{
        fontFamily: GD, fontStyle: 'italic', fontSize: 22,
        color: 'rgba(242,239,234,0.4)', marginBottom: 12,
      }}>
        The feed is being curated
      </div>
      <p style={{ fontFamily: NU, fontSize: 13, color: MUTED, maxWidth: 360, margin: '0 auto 24px', lineHeight: 1.7 }}>
        Members are sharing insights, venue spotlights, and industry intelligence. The first posts will appear shortly.
      </p>
      {onPost && (
        <button
          onClick={onPost}
          style={{
            background: 'transparent', border: `1px solid ${GOLD_BORDER}`,
            borderRadius: 4, color: GOLD, fontFamily: NU, fontSize: 10,
            fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase',
            padding: '12px 24px', cursor: 'pointer',
          }}
        >
          Be the first to post
        </button>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN FEED COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export default function CommunityFeed({ vendor = null, isMobile = false }) {
  const [posts,      setPosts]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [hasMore,    setHasMore]    = useState(true);
  const [filter,     setFilter]     = useState('all');
  const [refreshKey, setRefreshKey] = useState(0);
  const [activePost, setActivePost] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  // Reaction state
  const [likedIds, setLikedIds] = useState(new Set());
  const [savedIds, setSavedIds] = useState(new Set());
  const sessionKey = getSessionKey();

  const PER_PAGE = 9;
  const [fetchError, setFetchError] = useState(null);
  const pageRef = useRef(0);

  // ── Initial + filter-change fetch (no stale closure) ────────────────────────
  useEffect(() => {
    let cancelled = false;
    pageRef.current = 0;
    setLoading(true);
    setFetchError(null);
    setPosts([]);

    fetchPublishedPosts({
      page: 0,
      perPage: PER_PAGE,
      type: filter === 'all' ? undefined : filter,
    }).then(({ data, error }) => {
      if (cancelled) return;
      if (error) { setFetchError(error.message); setLoading(false); return; }
      const incoming = data || [];
      setPosts(incoming);
      setHasMore(incoming.length === PER_PAGE);
      pageRef.current = 1;
      setLoading(false);
      if (incoming.length > 0) {
        fetchSessionReactions(sessionKey, incoming.map(p => p.id))
          .then(({ likes, saves }) => {
            if (cancelled) return;
            setLikedIds(new Set(likes));
            setSavedIds(new Set(saves));
          }).catch(() => {});
      }
    }).catch(err => {
      if (!cancelled) { setFetchError(err.message); setLoading(false); }
    });

    return () => { cancelled = true; };
  }, [filter, refreshKey, sessionKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Load more (append next page) ────────────────────────────────────────────
  const load = useCallback((reset = false) => {
    if (reset) {
      setRefreshKey(k => k + 1); // force the useEffect to re-run
      return;
    }
    const nextPage = pageRef.current;
    setLoading(true);
    fetchPublishedPosts({
      page: nextPage,
      perPage: PER_PAGE,
      type: filter === 'all' ? undefined : filter,
    }).then(({ data, error }) => {
      if (error) { setFetchError(error.message); setLoading(false); return; }
      const incoming = data || [];
      setPosts(prev => [...prev, ...incoming]);
      setHasMore(incoming.length === PER_PAGE);
      pageRef.current = nextPage + 1;
      setLoading(false);
    }).catch(err => { setFetchError(err.message); setLoading(false); });
  }, [filter]);

  // ── React to post ───────────────────────────────────────────────────────────
  const handleLike = useCallback(async (postId) => {
    const wasLiked = likedIds.has(postId);
    // Optimistic update
    setLikedIds(prev => {
      const next = new Set(prev);
      wasLiked ? next.delete(postId) : next.add(postId);
      return next;
    });
    setPosts(prev => prev.map(p => p.id === postId
      ? { ...p, likes_count: (p.likes_count || 0) + (wasLiked ? -1 : 1) }
      : p
    ));
    await reactToPost(postId, 'like', sessionKey);
  }, [likedIds, sessionKey]);

  const handleSave = useCallback(async (postId) => {
    const wasSaved = savedIds.has(postId);
    setSavedIds(prev => {
      const next = new Set(prev);
      wasSaved ? next.delete(postId) : next.add(postId);
      return next;
    });
    setPosts(prev => prev.map(p => p.id === postId
      ? { ...p, saves_count: (p.saves_count || 0) + (wasSaved ? -1 : 1) }
      : p
    ));
    await reactToPost(postId, 'save', sessionKey);
  }, [savedIds, sessionKey]);

  // ── Split posts: featured + grid ────────────────────────────────────────────
  const featuredPost = posts.find(p => p.is_featured) || posts[0] || null;
  const gridPosts    = featuredPost ? posts.filter(p => p.id !== featuredPost.id) : [];

  return (
    <div>
      {/* ── Section header ── */}
      <div style={{
        display: 'flex', alignItems: 'flex-end',
        justifyContent: 'space-between',
        flexWrap: 'wrap', gap: 16,
        marginBottom: 28,
      }}>
        <div>
          <div style={{
            fontFamily: NU, fontSize: 9, fontWeight: 700,
            letterSpacing: '0.3em', textTransform: 'uppercase',
            color: GOLD, marginBottom: 8,
          }}>
            ◈ Live Feed
          </div>
          <h2 style={{
            fontFamily: GD, fontStyle: 'italic', fontWeight: 300,
            fontSize: isMobile ? 26 : 32, color: CREAM, margin: 0, lineHeight: 1.2,
          }}>
            From the Community
          </h2>
        </div>

        {/* Right side: refresh + post CTA */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <button
          onClick={() => load(true)}
          disabled={loading}
          style={{
            background: 'transparent',
            border: `1px solid ${BORDER2}`,
            borderRadius: 4, color: MUTED,
            fontFamily: NU, fontSize: 9, fontWeight: 600,
            letterSpacing: '0.12em', textTransform: 'uppercase',
            padding: '7px 14px', cursor: loading ? 'default' : 'pointer',
            opacity: loading ? 0.4 : 1, transition: 'all 0.15s',
          }}
          onMouseEnter={e => { if (!loading) e.currentTarget.style.borderColor = BORDER2; }}
        >
          {loading ? '◈' : '↺ Refresh'}
        </button>

        {/* Post CTA (vendor only) */}
        {vendor && (
          <button
            onClick={() => setShowCreate(true)}
            style={{
              background: 'transparent',
              border: `1px solid ${GOLD_BORDER}`,
              borderRadius: 4, color: GOLD,
              fontFamily: NU, fontSize: 9, fontWeight: 700,
              letterSpacing: '0.2em', textTransform: 'uppercase',
              padding: '10px 20px', cursor: 'pointer',
              transition: 'background 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = GOLD_DIM; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
          >
            + Share to Community
          </button>
        )}
        </div>
      </div>

      {/* ── Fetch error ── */}
      {fetchError && (
        <div style={{
          marginBottom: 16, padding: '10px 14px',
          background: 'rgba(248,113,113,0.08)',
          border: '1px solid rgba(248,113,113,0.2)',
          borderRadius: 5, fontFamily: NU, fontSize: 11,
          color: '#f87171', lineHeight: 1.5,
        }}>
          {fetchError}
        </div>
      )}

      {/* ── Filter pills ── */}
      <div style={{
        display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 32,
        borderBottom: `1px solid ${BORDER}`, paddingBottom: 20,
      }}>
        {FILTER_OPTIONS.map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            style={{
              background: filter === f.key ? GOLD_DIM : 'transparent',
              border: `1px solid ${filter === f.key ? GOLD_BORDER : BORDER}`,
              borderRadius: 20,
              color: filter === f.key ? GOLD : MUTED,
              fontFamily: NU, fontSize: 10, fontWeight: filter === f.key ? 700 : 400,
              padding: '5px 14px', cursor: 'pointer',
              transition: 'all 0.12s',
              letterSpacing: '0.04em',
            }}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* ── Feed content ── */}
      {loading && posts.length === 0 ? (
        <div style={{
          padding: '48px 0', textAlign: 'center',
          fontFamily: NU, fontSize: 12, color: MUTED,
          letterSpacing: '0.2em',
        }}>
          ◈ Loading
        </div>
      ) : posts.length === 0 ? (
        <EmptyState onPost={vendor ? () => setShowCreate(true) : null} />
      ) : (
        <>
          {/* Featured post */}
          {featuredPost && (
            <div style={{ marginBottom: 20 }}>
              <FeaturedPostCard
                post={featuredPost}
                liked={likedIds.has(featuredPost.id)}
                saved={savedIds.has(featuredPost.id)}
                onLike={() => handleLike(featuredPost.id)}
                onSave={() => handleSave(featuredPost.id)}
                onClick={() => setActivePost(featuredPost)}
              />
            </div>
          )}

          {/* Grid */}
          {gridPosts.length > 0 && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
              gap: 16, marginBottom: 28,
            }}>
              {gridPosts.map(post => (
                <StandardPostCard
                  key={post.id}
                  post={post}
                  liked={likedIds.has(post.id)}
                  saved={savedIds.has(post.id)}
                  onLike={() => handleLike(post.id)}
                  onSave={() => handleSave(post.id)}
                  onClick={() => setActivePost(post)}
                />
              ))}
            </div>
          )}

          {/* Load more */}
          {hasMore && (
            <div style={{ textAlign: 'center', paddingTop: 8, paddingBottom: 8 }}>
              <button
                onClick={() => load(false)}
                disabled={loading}
                style={{
                  background: 'transparent',
                  border: `1px solid ${BORDER2}`,
                  borderRadius: 4, color: WARM,
                  fontFamily: NU, fontSize: 10, fontWeight: 600,
                  letterSpacing: '0.18em', textTransform: 'uppercase',
                  padding: '12px 28px', cursor: loading ? 'default' : 'pointer',
                  opacity: loading ? 0.5 : 1, transition: 'all 0.15s',
                }}
                onMouseEnter={e => { if (!loading) e.currentTarget.style.borderColor = GOLD_BORDER; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = BORDER2; }}
              >
                {loading ? '◈ Loading...' : 'See more from the community'}
              </button>
            </div>
          )}

          {/* Base CTA, always take readers into the full community feed */}
          <div style={{ textAlign: 'center', marginTop: 8, paddingTop: 28, borderTop: `1px solid ${BORDER}` }}>
            <button
              onClick={() => { window.location.href = '/community/feed'; }}
              style={{
                background: 'transparent', border: `1px solid ${GOLD_BORDER}`, borderRadius: 4,
                color: GOLD, fontFamily: NU, fontSize: 10, fontWeight: 700,
                letterSpacing: '0.2em', textTransform: 'uppercase',
                padding: '14px 34px', cursor: 'pointer', transition: 'background 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = GOLD_DIM; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
            >
              Enter the Community Feed →
            </button>
          </div>
        </>
      )}

      {/* ── Modals ── */}
      {activePost && (
        <PostDetailModal
          post={activePost}
          liked={likedIds.has(activePost.id)}
          saved={savedIds.has(activePost.id)}
          onLike={() => handleLike(activePost.id)}
          onSave={() => handleSave(activePost.id)}
          onClose={() => setActivePost(null)}
        />
      )}

      {showCreate && (
        <CreatePostModal
          vendor={vendor}
          onClose={() => setShowCreate(false)}
          onSubmitted={() => {
            setShowCreate(false);
            // Don't reload, post is pending review anyway
          }}
        />
      )}
    </div>
  );
}
